import React, { useEffect, useState } from 'react';
import MarketRegisterModal from '../mofamily/MarketRegisterModal';
import { FamilyAvatar } from '../../components/FamilyAvatar';

/**
 * 마켓 첫 화면의 "매물 등록" — 교환(바터) 외 세 유형(광고 받기·미디어 찾기·구성원 모집)은 기존
 * 모패밀리에 붙는 매물이라 등록 폼(MarketRegisterModal)이 모패밀리 해시를 요구한다. 지금까지는
 * 모패밀리 상세에서만 열 수 있어 진입점이 둘로 갈라져 찾기 어려웠다(2026-10-21 피드백). 여기서는
 * "어느 모패밀리에 걸지"를 먼저 고르고 같은 폼을 연다. 소유한 일반 모패밀리만 후보(바터용·비소유 제외).
 * 후보가 하나면 선택 단계를 건너뛴다.
 */
type Side = 'seeking_advertiser' | 'seeking_media' | 'seeking_member';

interface FamilyOption {
 moFamilyHash: string;
 name: string;
 imageUrl?: string | null;
 isDefault?: boolean;
 totalSlots?: number;
}

interface Props {
 initialSide?: Side;
 onClose: () => void;
 onCreated?: () => void;
}

const ListingCreateModal: React.FC<Props> = ({ initialSide, onClose, onCreated }) => {
 const [families, setFamilies] = useState<FamilyOption[] | null>(null);
 const [error, setError] = useState<string | null>(null);
 const [picked, setPicked] = useState<string | null>(null);

 useEffect(() => {
  let alive = true;
  fetch('/mofamily/my-families', { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (!alive) return;
    if (!j?.success) { setError(j?.error || '모패밀리 목록을 불러오지 못했습니다.'); setFamilies([]); return; }
    const list: FamilyOption[] = (j.data as any[])
     .filter(f => (f.isOwner || f.role === 'owner') && (f.familyType ?? 'regular') !== 'barter')
     .map(f => ({ moFamilyHash: f.moFamilyHash, name: f.name, imageUrl: f.imageUrl, isDefault: f.isDefault, totalSlots: f.totalSlots }));
    setFamilies(list);
    if (list.length === 1) setPicked(list[0].moFamilyHash);
   })
   .catch(() => { if (alive) { setError('모패밀리 목록을 불러오지 못했습니다.'); setFamilies([]); } });
  return () => { alive = false; };
 }, []);

 if (picked) {
  return (
   <MarketRegisterModal
    moFamilyHash={picked}
    initialSide={initialSide}
    onClose={onClose}
    onCreated={() => { onCreated?.(); }}
   />
  );
 }

 return (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
   <div className="bg-white max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
    <div className="flex items-start justify-between gap-2 mb-1">
     <h2 className="text-lg font-bold">매물 등록</h2>
     <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
    </div>
    <p className="text-sm text-gray-500 mb-4">
     매물은 모패밀리에 붙습니다. 어느 모패밀리로 올릴지 먼저 골라 주세요. 소유한 일반 모패밀리만 보입니다.
    </p>

    {families === null && <div className="text-sm text-gray-400 py-6 text-center">불러오는 중…</div>}
    {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
    {families && families.length === 0 && !error && (
     <div className="text-sm text-gray-500 border border-dashed border-gray-300 p-4 text-center">
      올릴 수 있는 모패밀리가 없습니다. 모패밀리 메뉴에서 먼저 하나 만들어 주세요.
     </div>
    )}
    {families && families.length > 1 && (
     <ul className="divide-y divide-gray-100 border border-gray-200 max-h-80 overflow-y-auto">
      {families.map(f => (
       <li key={f.moFamilyHash}>
        <button
         type="button"
         onClick={() => setPicked(f.moFamilyHash)}
         className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-blue-50"
        >
         <FamilyAvatar name={f.name} familyHash={f.moFamilyHash} imageUrl={f.imageUrl} size={36} />
         <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium truncate">{f.name}{f.isDefault && <span className="ml-1 text-xs text-gray-400 font-normal">(개인 전용)</span>}</span>
          <span className="block text-xs text-gray-500">슬롯 {f.totalSlots ?? 0}개</span>
         </span>
         <span className="text-xs text-blue-600">선택 ›</span>
        </button>
       </li>
      ))}
     </ul>
    )}
   </div>
  </div>
 );
};

export default ListingCreateModal;
